import { fetchAuthSession } from 'aws-amplify/auth';

const API_URL = import.meta.env.VITE_API_URL

////////////////////////////////////////////////////////////////////////////////////////
//
//  Get Volunteer Data API
//
////////////////////////////////////////////////////////////////////////////////////////
export interface VolunteerData {
  id: string;
  eventstatus: string;
  subject: string;
  date_start: string;
  assigned_user_id: string;
  neighbour: {
      neighbour: string;
      homephone: string;
      mobilephone: string;
      email: string;
      summary: string;
  }
}


export const getVolunteerData = async (): Promise<VolunteerData[]> => {

    let cognitoToken = null;
    const session = await fetchAuthSession();
    if (session.tokens && session.tokens.idToken) {
        cognitoToken = session.tokens.idToken.toString();
    } else {
        throw new Error("No valid Cognito token found.");
    }

    if (!cognitoToken) {
        throw new Error("No Cognito token provided.");
    }

    // Construct the API URL with query parameters for pagination
    const url = new URL(API_URL+"/prod/volunteerdata");

    const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
            Authorization: `Bearer ${cognitoToken}`, // Pass the Cognito token in the Authorization header
        },
    });

    if (!response.ok) {
        if (response.status === 403) {
            throw new Error("403: Forbidden");
        }
        throw new Error(`Error fetching data: ${response.statusText}`);
    }

    const responseData = await response.json();


    return responseData;
};

////////////////////////////////////////////////////////////////////////////////////////
//
//  Get Call Data API
//
////////////////////////////////////////////////////////////////////////////////////////
export interface CallData {
    call_details: {
      status: string;
      date: string;
      contact_id: string;
      // Add other fields as needed
    };
    contact_details: {
      name: string;
      preferred_name: string;
      birthday: string;
      homephone: string;
      mobile: string;
      neighbour_summary: string;
      address: string[];
      annual_review_date: string;
      // Add other fields as needed
    };
    comments: string[];
  }


export const getCallData = async (call_id: string): Promise<CallData> => {
    let cognitoToken = null;
    const session = await fetchAuthSession();

    if (session.tokens && session.tokens.idToken) {
        cognitoToken = session.tokens.idToken.toString();
    } else {
        throw new Error("No valid Cognito token found.");
    }

    if (!cognitoToken) {
        throw new Error("No Cognito token provided.");
    }

    // Construct the API URL with the dynamic call_id
    const url = new URL(API_URL+`/prod/calldata/${call_id}`);

    const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
            Authorization: `Bearer ${cognitoToken}`, // Pass the Cognito token in the Authorization header
        },
    });

    if (!response.ok) {
        throw new Error(`Error fetching data: ${response.statusText}`);
    }

    const responseData = await response.json();

    return responseData;
};


////////////////////////////////////////////////////////////////////////////////////////
//
//  Post Call Data API
//
////////////////////////////////////////////////////////////////////////////////////////
export const postCallData = async (bodyData: any): Promise<any[]> => {
    let cognitoToken = null;
    const session = await fetchAuthSession();

    if (session.tokens && session.tokens.idToken) {
        cognitoToken = session.tokens.idToken.toString();
    } else {
        throw new Error("No valid Cognito token found.");
    }

    if (!cognitoToken) {
        throw new Error("No Cognito token provided.");
    }

    // Construct the API URL with the dynamic call_id
    const url = new URL(API_URL+`/prod/calldata`);

    const response = await fetch(url.toString(), {
        method: "POST",
        headers: {
            Authorization: `Bearer ${cognitoToken}`, // Pass the Cognito token in the Authorization header
        },
        body: JSON.stringify(bodyData)
    });

    if (!response.ok) {
        throw new Error(`Error fetching data: ${response.statusText}`);
    }

    const responseData = await response.json();

    return responseData;
};


////////////////////////////////////////////////////////////////////////////////////////
//
//  Get Call Planning Data API
//
////////////////////////////////////////////////////////////////////////////////////////
export const getCallPlanningData = async ({ startdate, enddate  }: { startdate: string, enddate: string }) => {
    let cognitoToken = null;
    const session = await fetchAuthSession();
  
    if (session.tokens && session.tokens.idToken) {
      cognitoToken = session.tokens.idToken.toString();
    } else {
      throw new Error("No valid Cognito token found.");
    }
  
    if (!cognitoToken) {
      throw new Error("No Cognito token provided.");
    }
  
    const url = new URL(API_URL+`/prod/callplanning`);
    if (startdate) {
      url.searchParams.set("startdate", startdate);
    }
    if (enddate) {
      url.searchParams.set("enddate", enddate);
    }
  
    // ⏱️ Introduce a random delay between 0 and 1000 milliseconds
    // const delay = Math.random() * 5000;
    // await new Promise((resolve) => setTimeout(resolve, delay));
  
    const response = await fetch(url.toString(), {
      method: "GET",
      headers: {
        Authorization: `Bearer ${cognitoToken}`,
      },
    });
  
    if (!response.ok) {
      throw new Error(`Error fetching data: ${response.statusText}`);
    }
  
    const responseData = await response.json();
    return responseData;
  };
  

////////////////////////////////////////////////////////////////////////////////////////
//
//  Post Call Planning Data API
//
////////////////////////////////////////////////////////////////////////////////////////
export const postCallPlanningData = async (bodyData: any[], rearrangement: string = "false") => {
  let cognitoToken = null;
  const session = await fetchAuthSession();

  if (session.tokens && session.tokens.idToken) {
    cognitoToken = session.tokens.idToken.toString();
  } else {
    throw new Error("No valid Cognito token found.");
  }

  if (!cognitoToken) {
    throw new Error("No Cognito token provided.");
  }

  // Construct the API URL with rearrangement as a query param
  const url = new URL(API_URL+"/prod/callplanning");
  url.searchParams.set("rearrangement", rearrangement);

  // Set the request body
  const postBodyData = {
    events: bodyData,
  };

  const response = await fetch(url.toString(), {
    method: "POST",
    headers: {
      Authorization: `Bearer ${cognitoToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(postBodyData),
  });

  if (!response.ok) {
    throw new Error(`Error posting data: ${response.statusText}`);
  }

  const responseData = await response.json();
  return responseData;
};



////////////////////////////////////////////////////////////////////////////////////////
//
//  Get Volunteer Availability API
//
////////////////////////////////////////////////////////////////////////////////////////  
export const getAllVolunteerAvailability = async ({ startdate, enddate  }: { startdate: string, enddate: string }) => {
    let cognitoToken = null;
    const session = await fetchAuthSession();
  
    if (session.tokens && session.tokens.idToken) {
      cognitoToken = session.tokens.idToken.toString();
    } else {
      throw new Error("No valid Cognito token found.");
    }
  
    if (!cognitoToken) {
      throw new Error("No Cognito token provided.");
    }
  
    // Construct the API URL
    const url = new URL(API_URL+`/prod/volunteeravailabilityall`);
  
    // Add the date query parameter if provided
    if (startdate) {
      url.searchParams.set("startdate", startdate);
    }
    if (enddate) {
      url.searchParams.set("enddate", enddate);
    }

    // ⏱️ Introduce a random delay between 0 and 1000 milliseconds
    const delay = Math.random() * 5000;
    await new Promise((resolve) => setTimeout(resolve, delay));

    const response = await fetch(url.toString(), {
      method: "GET",
      headers: {
        Authorization: `Bearer ${cognitoToken}`,
      },
    });
  
    if (!response.ok) {
      throw new Error(`Error fetching data: ${response.statusText}`);
    }
  
    const responseData = await response.json();
    return responseData;
  };


////////////////////////////////////////////////////////////////////////////////////////
//
//  Post Specific Volunteer Availability API
//
////////////////////////////////////////////////////////////////////////////////////////
export const postVolunteerAvailability = async (bodyData: any[]) => {
  let cognitoToken = null;
  const session = await fetchAuthSession();

  if (session.tokens && session.tokens.idToken) {
      cognitoToken = session.tokens.idToken.toString();
  } else {
      throw new Error("No valid Cognito token found.");
  }

  if (!cognitoToken) {
      throw new Error("No Cognito token provided.");
  }

  // Construct the API URL
  const url = new URL(API_URL+"/prod/volunteeravailability");

  // Set the request body
  const postBodyData = {
      events: bodyData
  }

  const response = await fetch(url.toString(), {
      method: "POST",
      headers: {
          Authorization: `Bearer ${cognitoToken}`, // Pass the Cognito token in the Authorization header
          "Content-Type": "application/json", // Specify JSON content type
      },
      body: JSON.stringify(postBodyData), // Pass the array as the request body
  });

  if (!response.ok) {
      throw new Error(`Error posting data: ${response.statusText}`);
  }

  const responseData = await response.json();

  return responseData;
};


////////////////////////////////////////////////////////////////////////////////////////
//
//  Get Volunteer Availability API
//
////////////////////////////////////////////////////////////////////////////////////////
export const getVolunteerAvailability = async (): Promise<any[]> => {
  let cognitoToken = null;
  const session = await fetchAuthSession();

  if (session.tokens && session.tokens.idToken) {
    cognitoToken = session.tokens.idToken.toString();
  } else {
    throw new Error("No valid Cognito token found.");
  }

  if (!cognitoToken) {
    throw new Error("No Cognito token provided.");
  }

  // Construct the API URL
  const url = new URL(API_URL+"/prod/volunteeravailability");

  const response = await fetch(url.toString(), {
    method: "GET",
    headers: {
      Authorization: `Bearer ${cognitoToken}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Error fetching data: ${response.statusText}`);
  }

  const responseData = await response.json();
  return responseData;
};

////////////////////////////////////////////////////////////////////////////////////////
//
//  Get Portal Users API
//
////////////////////////////////////////////////////////////////////////////////////////
export const getUsers = async (): Promise<any[]> => {
  let cognitoToken = null;
  const session = await fetchAuthSession();

  if (session.tokens && session.tokens.idToken) {
    cognitoToken = session.tokens.idToken.toString();
  } else {
    throw new Error("No valid Cognito token found.");
  }

  const url = new URL(API_URL+`/prod/users`);

  const response = await fetch(url.toString(), {
    method: "GET",
    headers: {
      Authorization: `Bearer ${cognitoToken}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Error fetching data: ${response.statusText}`);
  }

  const responseData = await response.json();
  return responseData.users; // 👈 just return the users array
};


////////////////////////////////////////////////////////////////////////////////////////
//
//  Get Portal Users API
//
////////////////////////////////////////////////////////////////////////////////////////
export const postUserAction  = async (bodyData: {}) => {
  let cognitoToken = null;
  const session = await fetchAuthSession();

  if (session.tokens && session.tokens.idToken) {
    cognitoToken = session.tokens.idToken.toString();
  } else {
    throw new Error("No valid Cognito token found.");
  }

  const url = new URL(API_URL+`/prod/users`);

  const response = await fetch(url.toString(), {
    method: "POST",
    headers: {
        Authorization: `Bearer ${cognitoToken}`, // Pass the Cognito token in the Authorization header
        "Content-Type": "application/json", // Specify JSON content type
    },
    body: JSON.stringify(bodyData), // Pass the array as the request body
});

  if (!response.ok) {
    throw new Error(`Error fetching data: ${response.statusText}`);
  }

  const responseData = await response.json();
  return responseData.users; // 👈 just return the users array
};
